import React, { ReactNode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { Satellite, DotStatus, OrbitStatusMeta } from '../../../../types/mission';
import CustomToolTip from '../../Common/CustomToolTip';
import TreeItem, { TreeItemContext } from './TreeItem';
import styles from '../../LeftSideBar.module.scss';

const CaretIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 120ms linear', flexShrink: 0 }}
  >
    <rect width="16" height="15.3333" fill="black" fillOpacity="0.01" />
    <path d="M8.86236 9.63642C8.56947 9.92931 8.09459 9.92931 7.8017 9.63642L4.61236 6.44708C4.13989 5.9746 4.47451 5.16675 5.14269 5.16675L11.5214 5.16675C12.1895 5.16675 12.5242 5.9746 12.0517 6.44708L8.86236 9.63642Z" fill="currentColor" />
  </svg>
);

const SatGroupIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M5.43499 2.06419C5.56386 2.26322 5.78786 2.39493 6.04262 2.39493C6.29922 2.39493 6.52461 2.26132 6.65302 2.05987M5.43499 2.06419C5.36178 1.95111 5.31928 1.81631 5.31928 1.67159C5.31928 1.27209 5.64313 0.948242 6.04262 0.948242C6.44211 0.948242 6.76596 1.27209 6.76596 1.67159C6.76596 1.81448 6.72453 1.94769 6.65302 2.05987M5.43499 2.06419C4.89832 2.78912 4.52344 4.44043 4.52344 6.36151C4.52344 8.95168 5.20494 11.0514 6.04561 11.0514C6.50863 11.0514 6.92336 10.4145 7.20254 9.40942M6.65302 2.05987C6.8691 2.34992 7.05908 2.78999 7.2091 3.33738M7.4908 4.88489C7.53727 5.31689 7.56391 5.77675 7.56738 6.25421" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.28606 5.0432C7.52922 3.74812 9.68841 3.28844 10.1087 4.01648C10.5174 4.7243 9.13202 6.29418 6.99337 7.57098M3.16738 6.55924C2.60932 7.0648 2.21065 7.55418 2.02727 7.96535M4.6826 8.67334C3.84319 8.9691 3.11336 9.09885 2.61378 9.03688M2.61378 9.03688C2.67112 8.93324 2.70376 8.81403 2.70376 8.6872C2.70376 8.30344 2.40493 7.98949 2.02727 7.96535C2.01178 7.96436 1.99616 7.96385 1.98042 7.96385C1.58093 7.96385 1.25708 8.28771 1.25708 8.6872C1.25708 9.08669 1.58093 9.41054 1.98042 9.41054C2.25308 9.41054 2.4905 9.25968 2.61378 9.03688Z" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.7308 4.06637C3.35919 3.57519 2.27123 3.52167 1.98569 4.01624C1.56536 4.74428 3.04305 6.38435 5.2862 7.67943C6.95118 8.64071 8.56989 9.14173 9.46562 9.03843M10.0753 7.98356C9.69111 8.00082 9.38495 8.31774 9.38495 8.70616C9.38495 8.82595 9.41407 8.93894 9.46562 9.03843M10.0753 7.98356C10.0862 7.98307 10.0972 7.98282 10.1083 7.98282C10.5078 7.98282 10.8316 8.30667 10.8316 8.70616C10.8316 9.10565 10.5078 9.4295 10.1083 9.4295C9.82859 9.4295 9.58597 9.27075 9.46562 9.03843M10.0753 7.98356C9.93084 7.64979 9.64234 7.26326 9.24018 6.85774" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SatIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M7.38489 2.0577L5.67255 0.345363C5.61172 0.28454 5.5127 0.284256 5.45563 0.348614C4.98336 0.881179 4.77115 1.56372 4.81869 2.2321M4.81869 2.2321C4.8612 2.82971 5.11137 3.41599 5.56899 3.8736C6.00192 4.30654 6.64661 4.56249 7.2105 4.6239C7.90903 4.69997 8.54737 4.48325 9.09456 3.9876C9.15832 3.92985 9.15805 3.83087 9.09723 3.77005L7.38489 2.0577M7.38489 2.0577L8.31883 1.12376M4.81869 2.2321L3.78222 3.26857M7.2105 4.6239L6.17403 5.66037M3.78222 3.26857L2.32021 1.80655C2.25938 1.74572 2.16077 1.74572 2.09995 1.80655L0.346182 3.56031C0.285359 3.62114 0.285359 3.71975 0.346182 3.78057L1.8082 5.24259M3.78222 3.26857L1.8082 5.24259M1.8082 5.24259L1.39323 5.65757C0.987741 6.06305 0.98774 6.72047 1.39322 7.12595L2.31664 8.04937C2.72212 8.45485 3.37955 8.45485 3.78503 8.04937L4.20001 7.63439M6.17403 5.66037L4.20001 7.63439M6.17403 5.66037L7.63605 7.12239C7.69687 7.18321 7.69687 7.28183 7.63605 7.34265L5.88228 9.09641C5.82146 9.15724 5.72285 9.15724 5.66202 9.09641L4.20001 7.63439" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" />
  </svg>
);

const EyeOnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1 6.5C2.5 3.5 5 2 6.5 2S10.5 3.5 12 6.5C10.5 9.5 8 11 6.5 11S2.5 9.5 1 6.5z" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="6.5" cy="6.5" r="1.8" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1 1l11 11M5 2.5C5.6 2.2 6.1 2 6.5 2 8 2 10.5 3.5 12 6.5c-.5 1-1.2 1.9-2 2.5M3.5 4C2.4 4.8 1.5 5.7 1 6.5 2.5 9.5 5 11 6.5 11c1 0 2-.4 2.9-1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const MoreIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <circle cx="6.5" cy="6.5" r="0.9" fill="currentColor" />
    <circle cx="6.5" cy="3.2" r="0.9" fill="currentColor" />
    <circle cx="6.5" cy="9.8" r="0.9" fill="currentColor" />
  </svg>
);


const DOT_COLORS: Record<DotStatus, string> = {
  ok: 'var(--accent, #ccf54e)',
  warn: 'rgb(218, 98, 33)',
  info: 'rgb(80, 160, 255)',
  none: 'var(--fg5, rgba(255,255,255,0.2))',
};

const SHOW_ORBIT_COLOR_INDICATOR = true;

interface TreeGroupProps {
  id: string;
  name: string;
  icon?: ReactNode;
  isTleGroup?: boolean;
  dot: DotStatus;
  expanded: boolean;
  active: boolean;
  children: Satellite[];
  selectedId: string | null;
  onToggleExpand: () => void;
  onToggleActive: () => void;
  onSelectChild: (id: string, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  onToggleChildActive: (id: string) => void;
  onDeleteGroup?: () => void | Promise<void>;
  onRequestDeleteGroup?: () => void;
  onDuplicateGroup?: () => void | Promise<void>;
  onEditGroup?: () => void;
  onDeleteChild?: (id: string) => void | Promise<void>;
  onDuplicateChild?: (id: string) => void | Promise<void>;
  onEditChild?: (id: string) => void;
  onHoverChild?: (id: string | null) => void;
  onHoverGroup?: (id: string | null) => void;
  selected?: boolean;
  onSelectGroup?: (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  onContextMenuGroup?: (event: React.MouseEvent<HTMLDivElement>) => void;
  selectedChildIds?: string[];
  onContextMenuChild?: (id: string, event: React.MouseEvent<HTMLDivElement>) => void;
  orbitStatus?: OrbitStatusMeta;
  disabled?: boolean;
  unusedInReport?: boolean;
  unusedChildIds?: string[];
  orbitColour?: string | null;
}

const TreeGroup: React.FC<TreeGroupProps> = ({
  id, name, icon, isTleGroup, dot, expanded, active, children, selectedId,
  onToggleExpand, onToggleActive, onSelectChild, onToggleChildActive,
  onDeleteGroup, onRequestDeleteGroup, onDuplicateGroup, onEditGroup, onDeleteChild, onDuplicateChild, onEditChild, onHoverChild, onHoverGroup,
  selected, onSelectGroup, onContextMenuGroup, selectedChildIds = [], onContextMenuChild, disabled = false,
  unusedInReport = false, unusedChildIds = [], orbitColour,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const [busy, setBusy] = useState<'idle' | 'duplicating'>('idle');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { openMenuId: parentOpenMenuId, setOpenMenuId: parentSetOpenMenuId } = React.useContext(TreeItemContext) || { openMenuId: null, setOpenMenuId: () => { } };

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: Event) => {
      const target = e.target as Node;
      // Close menu if click is outside the menu ref and the trigger button
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setMenuOpen(false);
        setOpenMenuId(null);
        parentSetOpenMenuId(null);
      }
    };
    // Use capture phase to catch events from canvas/Cesium viewer
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [menuOpen, parentSetOpenMenuId]);

  useEffect(() => {
    if (parentOpenMenuId && parentOpenMenuId !== id && menuOpen) {
      setMenuOpen(false);
    }
  }, [parentOpenMenuId, id, menuOpen]);

  const runDuplicate = async () => {
    setMenuOpen(false);
    setBusy('duplicating');
    try {
      await onDuplicateGroup?.();
    } finally {
      setBusy('idle');
    }
  };

  const runEdit = () => {
    setMenuOpen(false);
    onEditGroup?.();
  };

  return (
    <div className={styles.treeGroup}>
      <div
        className={`${styles.groupHeader} ${(selected || menuOpen) ? styles.selected : ''} ${!active ? styles.inactive : ''} ${busy !== 'idle' ? styles.treeItemDeleting : ''} ${unusedInReport ? styles.unusedInReport : ''}`}
        onClick={unusedInReport ? undefined : (onSelectGroup ?? onToggleExpand)}
        role="button"
        tabIndex={unusedInReport ? -1 : 0}
        onKeyDown={e => {
          if (unusedInReport) return;
          if (e.key === 'Enter') onToggleExpand();
        }}
        onContextMenu={onContextMenuGroup}
        onMouseEnter={() => onHoverGroup?.(id)}
        onMouseLeave={() => onHoverGroup?.(null)}
      >
        {busy !== 'idle' ? (
          <>
            <span className={styles.rowSpinner} />
            <span className={styles.groupName} style={{ opacity: 0.4 }}>
              {busy === 'duplicating' ? `Duplicating ${name}` : name}
            </span>
          </>
        ) : (
          <>
            <span onClick={e => { e.stopPropagation(); onToggleExpand(); }}>
              <CaretIcon open={expanded} />
            </span>
            <div className={isTleGroup ? styles.tleGroupIcon : styles.groupHeaderIcon}>
              {icon ?? <SatGroupIcon />}
            </div>
            <span
              className={styles.dot}
              style={{
                background: active ? DOT_COLORS[dot] : 'var(--fg5, rgba(255,255,255,0.2))',
                boxShadow: active && dot !== 'none' ? `0 0 5px ${DOT_COLORS[dot]}` : 'none',
              }}
            />
            {SHOW_ORBIT_COLOR_INDICATOR && orbitColour && (
              <span
                className={styles.colorIndicator}
                style={{
                  backgroundColor: orbitColour.includes('#') ? orbitColour : `rgba(${orbitColour.replace('[', '').replace(']', '')})`,
                }}
              />
            )}
            <span className={styles.itemNameWrapper} style={{ paddingLeft: '8px' }}>
              <CustomToolTip title={name} placement="right" className={styles.groupName}>
                {name}
              </CustomToolTip>
            </span>
            <div className={`${styles.actions} ${menuOpen ? styles.visible : ''}`} onClick={e => e.stopPropagation()}>
              <button
                className={`${styles.actionBtn} ${!active ? styles.eyeOff : ''}`}
                type="button"
                title={active ? 'Hide group' : 'Show group'}
                onClick={onToggleActive}
              >
                {active ? <EyeOnIcon /> : <EyeOffIcon />}
              </button>
              {!disabled && (
                <div className={styles.moreWrap}>
                  <button
                    className={styles.actionBtn}
                    type="button"
                    title="More"
                    ref={triggerRef}
                    onClick={(e) => {
                      const newState = !menuOpen;
                      setMenuOpen(newState);
                      setOpenMenuId(newState ? id : null);
                      parentSetOpenMenuId(newState ? id : null);
                      if (newState) {
                        setTriggerRect(e.currentTarget.getBoundingClientRect());
                      }
                    }}
                  >
                    <MoreIcon />
                  </button>
                  {menuOpen && triggerRect && ReactDOM.createPortal(
                    <div
                      className={styles.rowMenu}
                      ref={menuRef}
                      style={{
                        position: 'fixed',
                        top: triggerRect.top,
                        left: triggerRect.right + 8,
                        right: 'auto',
                        zIndex: 99999,
                      }}
                    >
                      {onEditGroup && !isTleGroup && (
                        <button className={styles.rowMenuItem} type="button" onClick={runEdit} disabled={!active} title={!active ? "Group must be visible to edit" : undefined}>
                          <EditIcon />
                          <span>Edit</span>
                        </button>
                      )}

                      {onDuplicateGroup && (
                        <button className={styles.rowMenuItem} type="button" onClick={runDuplicate} disabled={!active} title={!active ? "Group must be visible to duplicate" : undefined}>
                          <DuplicateIcon />
                          <span>Duplicate</span>
                        </button>
                      )}

                      {onDeleteGroup && (
                        <button
                          className={`${styles.rowMenuItem} ${styles.rowMenuDanger}`}
                          type="button"
                          onClick={() => { setMenuOpen(false); setOpenMenuId(null); parentSetOpenMenuId(null); onRequestDeleteGroup?.(); }}
                          disabled={!active}
                          title={!active ? "Group must be visible to delete" : undefined}
                        >
                          <TrashIcon />
                          <span>Delete</span>
                        </button>
                      )}

                    </div>,
                    document.body
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {expanded && (
        <TreeItemContext.Provider value={{ openMenuId, setOpenMenuId }}>
          <div className={`${styles.groupBody} ${!active ? styles.dimmed : ''}`}>
            {children.map(sat => (
              <div className={styles.groupChild} key={sat.id}>
                <TreeItem
                  id={sat.id}
                  name={sat.name}
                  dot={sat.dot}
                  active={sat.active}
                  icon={<SatIcon />}
                  grouped
                  selected={selectedId === sat.id || selectedChildIds.includes(sat.id)}
                  onSelect={(e) => onSelectChild(sat.id, e)}
                  onToggleActive={() => onToggleChildActive(sat.id)}
                  orbitStatus={sat.orbitPointStatus}
                  onEdit={onEditChild ? () => onEditChild(sat.id) : undefined}
                  onDuplicate={onDuplicateChild ? () => onDuplicateChild(sat.id) : undefined}
                  onDelete={onDeleteChild ? () => onDeleteChild(sat.id) : undefined}
                  onHover={onHoverChild}
                  onContextMenu={onContextMenuChild ? (e) => onContextMenuChild(sat.id, e) : undefined}
                  disabled={disabled}
                  unusedInReport={unusedChildIds.includes(sat.id)}
                  orbitColour={sat.orbitColour}
                />
              </div>
            ))}
          </div>
        </TreeItemContext.Provider>
      )}
    </div>
  );
};

export default TreeGroup;
